import {Button, createStyles, Group} from '@mantine/core';
import React, {useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {HOMEPAGE_ANALYTICS_ACTIONS} from 'constants/analytics-products/HomepageAnalyticsConstants';
import {Section} from 'constants/NavConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import strUtil from 'util/strUtil';
import useRecentWorklistsQuery from 'worklists/useRecentWorklistsQuery';
import {WorklistType, worklistTypeDisplayName} from 'worklists/WorklistConstants';
import WorklistCreateButton from 'worklists/WorklistCreateButton';
import {openWorklistListing} from 'worklists/WorklistUIActions';

import CardSection, {
  CardSectionEmptyState,
  CardSectionMessageRow,
  CardSectionRowList,
  CardSectionRowProps,
  renderQueryLoadingOrError,
} from './CardSection';
import {
  HOMEPAGE_SECTION_TEST_COMPONENT,
  INITIAL_ROWS_TO_DISPLAY,
  MAX_ROWS_TO_DISPLAY,
} from './HomepageConstants';
import {HOMEPAGE_SECTION_EMPTY_STATES} from './HomepageEmptyStates';
import {getHomepageWorklistOutlineIcon} from './HomepageIconUtil';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.WORKLISTS;

const useStyles = createStyles((theme) => ({
  worklistCreateButton: {
    '&&': {
      borderRadius: theme.radius.md,
    },
  },
}));

export default function WorklistsSection(): JSX.Element {
  const {queryResultType, recentWorklists} = useRecentWorklistsQuery();
  const [showMore, setShowMore] = useState(false);
  const {classes} = useStyles();
  const worklistsSectionRows: Array<CardSectionRowProps> | undefined = recentWorklists
    ?.slice(0, showMore ? MAX_ROWS_TO_DISPLAY : INITIAL_ROWS_TO_DISPLAY)
    .map((worklist) => ({
      id: worklist.id,
      icon: getHomepageWorklistOutlineIcon(worklist.itemType as WorklistType),
      name: worklist.name,
      modifiedAt: worklist.modifiedAt,
      count: worklist.items.length,
      onClick: () => {
        trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.CLICKED_RECENT_WORKLIST);
        openWorklistListing(worklist);
      },
      rightContent: strUtil.toSentenceCase(worklistTypeDisplayName(worklist.itemType)),
    }));
  const loadingOrError = renderQueryLoadingOrError(
    {type: queryResultType},
    'Error loading worklists',
    TEST_COMPONENT
  );
  return (
    <CardSection
      title="Worklists"
      dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}
      rightContent={
        <Group>
          <WorklistCreateButton newButton={true} className={classes.worklistCreateButton} />{' '}
          <Button
            size="md"
            radius="md"
            // eslint-disable-next-line no-restricted-syntax -- no custom variant matches the design (yet)
            variant="default"
            color="gray"
            onClick={() => {
              SidepanelActions.openSection(Section.WORKLISTS);
            }}
            {...getTestProps({component: TEST_COMPONENT, element: 'seeAllButton'})}
          >
            See all
          </Button>
        </Group>
      }
    >
      {loadingOrError}
      {queryResultType === QueryResultType.SUCCESS &&
        (!worklistsSectionRows!.length ? (
          <CardSectionEmptyState
            {...HOMEPAGE_SECTION_EMPTY_STATES.worklists}
            dataTestAttrs={{component: TEST_COMPONENT, element: 'messageRow', key: 'empty'}}
          />
        ) : (
          <CardSectionRowList
            rows={worklistsSectionRows!}
            rowDataTestAttrs={{component: TEST_COMPONENT, element: 'row'}}
          />
        ))}
      {recentWorklists != null && recentWorklists.length > INITIAL_ROWS_TO_DISPLAY && (
        <CardSectionMessageRow
          message={showMore ? 'Show less' : 'Show more'}
          onClick={() => {
            setShowMore(!showMore);
          }}
          dataTestAttrs={{component: TEST_COMPONENT, element: 'showMoreButton'}}
        />
      )}
    </CardSection>
  );
}
